import { type CSSProperties, useCallback, useEffect, useMemo, useState } from 'react'
import type { Combo } from '@/types/combo'
import styles from './CampaignWidgetManager.module.css'
import {
  WIDGET_AVULSO_PRESETS,
  clampWidgetExtraNumbers,
  computeComboDisplayPrice,
  formatWidgetBRL,
  isComboVisibleInWidget,
  parseApiMoney,
  widgetLimitsLabel,
} from './widgetModalPreviewLogic'

export type WidgetModalLivePreviewProps = {
  buttonColor: string
  buttonTextColor: string
  radiusPx: number
  unitPrice: number
  minExtra: number
  maxExtra: number | null
  combos: Combo[]
  /** Quando true, usa `combo_price` da API como no widget publicado (preço unitário igual ao salvo). */
  preferServerComboPrices: boolean
  isLoading: boolean
  errorMessage: string | null
}

type PurchaseMode = 'combo' | 'avulso'

type VisibleComboRow = {
  combo: Combo
  displayPrice: number
}

export function WidgetModalLivePreview({
  buttonColor,
  buttonTextColor,
  radiusPx,
  unitPrice,
  minExtra,
  maxExtra,
  combos,
  preferServerComboPrices,
  isLoading,
  errorMessage,
}: WidgetModalLivePreviewProps) {
  const visibleRows = useMemo((): VisibleComboRow[] => {
    return combos
      .filter((c) => c.is_active && isComboVisibleInWidget(c))
      .map((combo) => {
        const displayPrice = preferServerComboPrices
          ? parseApiMoney(combo.combo_price)
          : computeComboDisplayPrice(unitPrice, combo.quantity, combo.discount_percentage)
        return { combo, displayPrice }
      })
  }, [combos, unitPrice, preferServerComboPrices])

  const [mode, setMode] = useState<PurchaseMode>('avulso')
  const [selectedComboId, setSelectedComboId] = useState<number | null>(null)
  const [extraNumbers, setExtraNumbers] = useState(minExtra)

  useEffect(() => {
    setExtraNumbers((prev) => clampWidgetExtraNumbers(prev, minExtra, maxExtra))
  }, [minExtra, maxExtra])

  useEffect(() => {
    if (visibleRows.length > 0) {
      setMode('combo')
      setSelectedComboId(visibleRows[0].combo.id)
    } else {
      setMode('avulso')
      setSelectedComboId(null)
    }
  }, [visibleRows])

  const selectedRow = useMemo(
    () => visibleRows.find((r) => r.combo.id === selectedComboId) ?? null,
    [visibleRows, selectedComboId],
  )

  const avulsoTotal = unitPrice * extraNumbers
  const displayTotal = mode === 'combo' && selectedRow ? selectedRow.displayPrice : avulsoTotal

  const hasComboSelected = mode === 'combo' && selectedComboId !== null
  const hasAvulsoOk = mode === 'avulso' && extraNumbers >= minExtra
  const canCheckout = hasComboSelected || hasAvulsoOk

  const selectCombo = useCallback((id: number) => {
    setMode('combo')
    setSelectedComboId(id)
  }, [])

  const selectPreset = useCallback(
    (qty: number) => {
      setMode('avulso')
      setSelectedComboId(null)
      setExtraNumbers(clampWidgetExtraNumbers(qty, minExtra, maxExtra))
    },
    [minExtra, maxExtra],
  )

  const adjustExtra = useCallback(
    (delta: number) => {
      setMode('avulso')
      setSelectedComboId(null)
      setExtraNumbers((prev) => clampWidgetExtraNumbers(prev + delta, minExtra, maxExtra))
    },
    [minExtra, maxExtra],
  )

  const softTint = `${buttonColor}14`
  const vars = {
    '--wm-accent': buttonColor,
    '--wm-accent-soft': softTint,
    '--wm-btn-text': buttonTextColor,
    '--wm-radius': `${radiusPx}px`,
    '--wm-radius-sm': `${Math.max(6, Math.round(radiusPx * 0.75))}px`,
  } as CSSProperties

  if (isLoading) {
    return (
      <div className={styles.modalStage} style={vars}>
        <div className={styles.modalBackdrop} aria-hidden />
        <div className={styles.modalFrame}>
          <div className={styles.modalChrome}>
            <div className={styles.modalPreviewLoading} role="status">
              Carregando dados da campanha para a prévia…
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (errorMessage) {
    return (
      <div className={styles.modalStage} style={vars}>
        <div className={styles.modalBackdrop} aria-hidden />
        <div className={styles.modalFrame}>
          <div className={styles.modalChrome}>
            <p className={styles.modalPreviewError} role="alert">
              {errorMessage}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.modalStage} style={vars}>
      <div className={styles.modalBackdrop} aria-hidden />
      <div className={styles.modalFrame}>
        <div className={styles.modalChrome}>
          <button type="button" className={styles.modalCloseMock} aria-hidden tabIndex={-1}>
            ×
          </button>
          <h3 className={styles.modalTitleMock}>Escolha seu combo</h3>

          {unitPrice <= 0 && (
            <p className={styles.modalPreviewWarn} role="status">
              Defina um preço unitário válido na aba Valores para que os totais e os preços dos combos batam com o
              site publicado.
            </p>
          )}

          {visibleRows.length > 0 && (
            <div className={styles.comboListMock}>
              {visibleRows.map(({ combo, displayPrice }) => {
                const selected = mode === 'combo' && selectedComboId === combo.id
                return (
                  <button
                    key={combo.id}
                    type="button"
                    className={`${styles.comboRowMock} ${styles.comboRowClickable} ${selected ? styles.comboRowSelected : ''}`}
                    onClick={() => selectCombo(combo.id)}
                  >
                    <div className={styles.comboRowInfo}>
                      <span className={styles.comboRowTitle}>{combo.title}</span>
                      <span className={styles.comboRowMeta}>{combo.quantity} números da sorte</span>
                    </div>
                    <span className={styles.comboRowPrice}>{formatWidgetBRL(displayPrice)}</span>
                  </button>
                )
              })}
            </div>
          )}

          {visibleRows.length > 0 && <hr className={styles.modalDivider} />}

          <div className={styles.avulsoHeadMock}>
            <h4 className={styles.avulsoTitleMock}>Números avulsos</h4>
            <span className={styles.avulsoLimitsMock}>{widgetLimitsLabel(minExtra, maxExtra)}</span>
          </div>

          <div className={styles.presetsGridMock}>
            {WIDGET_AVULSO_PRESETS.map((preset) => {
              const disabled = maxExtra !== null && preset > maxExtra
              const selected = mode === 'avulso' && extraNumbers === preset
              return (
                <button
                  key={preset}
                  type="button"
                  disabled={disabled}
                  className={`${styles.presetBtnMock} ${selected ? styles.presetBtnSelected : ''}`}
                  onClick={() => selectPreset(preset)}
                >
                  +
                  {preset}
                </button>
              )
            })}
          </div>

          <div className={styles.stepperMock}>
            <button
              type="button"
              className={styles.stepperBtnInteractive}
              disabled={extraNumbers <= minExtra}
              onClick={() => adjustExtra(-1)}
              aria-label="Diminuir quantidade avulsa"
            >
              -
            </button>
            <span className={styles.stepperValueMock}>{extraNumbers}</span>
            <button
              type="button"
              className={styles.stepperBtnInteractive}
              disabled={maxExtra !== null && extraNumbers >= maxExtra}
              onClick={() => adjustExtra(1)}
              aria-label="Aumentar quantidade avulsa"
            >
              +
            </button>
          </div>

          <div className={styles.totalBarMock}>
            <span>Total</span>
            <strong>{formatWidgetBRL(displayTotal)}</strong>
          </div>

          <label className={styles.fieldLabelMock} htmlFor="widget-preview-email-live">
            Seu e-mail (para receber os números)
          </label>
          <input
            id="widget-preview-email-live"
            type="email"
            className={styles.inputMock}
            placeholder="exemplo@email.com"
            readOnly
            autoComplete="off"
            tabIndex={-1}
          />

          <button
            type="button"
            className={`${styles.pixBtnMock} ${!canCheckout ? styles.pixBtnMockDisabled : ''}`}
            disabled={!canCheckout}
            tabIndex={-1}
          >
            Gerar PIX
          </button>

          <p className={styles.modalPreviewFootnote}>
            Prévia interativa com os mesmos limites e presets do widget no site. Preço unitário e mínimos ou máximos de
            avulsos acompanham os campos da aba Valores mesmo antes de salvar. Títulos e descontos dos combos vêm do
            cadastro atual no servidor.
          </p>
        </div>
      </div>
    </div>
  )
}
